import { useMemo, useState } from 'react';
import { buildDrama } from '../lib/lifeDrama.js';
import { comparisonCandidates } from '../lib/compareCards.js';
import { runIdentity } from '../lib/storage.js';
import { manwonText } from '../lib/lifeResult.js';

// 순자산 겹쳐 보기: 이전(회색)과 이번(파랑), 갈라진 나이에 세로 점선
function Overlay({ chart }) {
  const { prev, cur, forkAge } = chart;
  if (!prev.length || !cur.length) return null;
  const W = 720, H = 280, L = 64, R = 22, T = 24, B = 32;
  const all = [...prev, ...cur];
  const minAge = Math.min(...all.map((p) => p.age)), maxAge = Math.max(...all.map((p) => p.age));
  const top = Math.max(1, ...all.map((p) => p.netWorth)) * 1.08, bottom = Math.min(0, ...all.map((p) => p.netWorth)) * 1.08;
  const x = (age) => L + ((age - minAge) / Math.max(1, maxAge - minAge)) * (W - L - R);
  const y = (v) => T + (1 - (v - bottom) / Math.max(1, top - bottom)) * (H - T - B);
  const path = (series) => series.map((p, i) => `${i ? 'L' : 'M'}${x(p.age)},${y(p.netWorth)}`).join(' ');
  const ticks = [bottom, (bottom + top) / 2, top].map((v) => Math.round(v));
  return <svg viewBox={`0 0 ${W} ${H}`} className="rgraph drama-graph" role="img" aria-label="이전과 이번 인생의 순자산 비교 그래프">
    {ticks.map((v) => <g key={v}><line x1={L} x2={W - R} y1={y(v)} y2={y(v)} stroke="#e7d8cf" strokeDasharray="3 5" /><text x={L - 12} y={y(v) + 4} fontSize="10" textAnchor="end" fill="#a58b7b">{Math.abs(v) >= 10000 ? `${Number((v / 10000).toFixed(1))}억` : `${v.toLocaleString()}만`}</text></g>)}
    {all.filter((p) => p.age % 10 === 0).filter((p, i, a) => a.findIndex((q) => q.age === p.age) === i).map((p) => <text key={p.age} x={x(p.age)} y={H - 6} fontSize="10" textAnchor="middle" fill="#8a867d">{p.age}세</text>)}
    {forkAge != null && forkAge >= minAge && forkAge <= maxAge && <g><line x1={x(forkAge)} x2={x(forkAge)} y1={T} y2={H - B} stroke="#d49a8e" strokeDasharray="4 4" /><text x={Math.min(W - 95, x(forkAge) + 5)} y={T - 8} fontSize="10" fill="#b98174">{forkAge}세 · 첫 차이</text></g>}
    <path d={path(prev)} fill="none" stroke="#c2a49b" strokeWidth="2" strokeDasharray="5 4" strokeLinecap="round" strokeLinejoin="round" />
    <path d={path(cur)} fill="none" stroke="#a38ab8" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" />
  </svg>;
}

/** 같은 출생 조건으로 산 이전 인생과 이번 인생을 극적으로 비교한다. 비교할 기록이 없으면 안내만 보인다. */
export default function LifeDrama({ run, lives, previous, onSelect }) {
  const [onlyChanged, setOnlyChanged] = useState(true);
  const candidates = useMemo(() => comparisonCandidates(run, lives), [run, lives]);
  const drama = useMemo(() => (previous ? buildDrama(run, previous, lives) : null), [run, previous, lives]);
  if (!drama) {
    return <section className="panel life-drama" aria-label="같은 출생 조건, 다른 선택">
      <h3>같은 출생 조건, 다른 선택</h3>
      <p className="muted">같은 조건으로 한 생을 더 마치면 이전 인생과 이번 인생을 나란히 비교할 수 있습니다. 완료한 기록은 이 브라우저에 보관됩니다.</p>
    </section>;
  }
  const { prev, cur } = drama;
  const changedRows = drama.rows.filter((r) => r.tone !== 'same');
  const shownRows = onlyChanged ? changedRows : [...changedRows, ...drama.rows.filter((r) => r.tone === 'same')];
  return <section className="panel life-drama" aria-label="같은 출생 조건, 다른 선택">
    <h3>같은 출생 조건, 다른 선택</h3>
    {candidates.length > 1 && <label className="result-compare-select">비교할 완료 기록
      <select value={runIdentity(previous)} onChange={(event) => onSelect(event.target.value)}>
        {candidates.map((life, index) => <option key={runIdentity(life)} value={runIdentity(life)}>{index + 1}번째 보관 기록 · {Math.max(0, life.age - 1)}세</option>)}
      </select>
    </label>}
    <div className="drama-hero" role="status">
      <b>{drama.headline}</b>
      {drama.fork && drama.fork.previous && drama.fork.current && <div className="drama-fork">
        <p><small>이전 선택</small>{drama.fork.previous}</p>
        <p className="drama-versus" aria-hidden="true">↔</p>
        <p><small>이번 선택</small>{drama.fork.current}</p>
      </div>}
    </div>

    {drama.biggest.length > 0 && <div className="drama-biggest" aria-label="가장 크게 달라진 것">
      {drama.biggest.map((item) => <div key={item.label} className={`drama-big drama-big-${item.up === null ? 'flat' : item.up ? 'up' : 'down'}`}>
        <small>{item.label}</small><b>{item.text}</b>
        <span>{typeof item.prev === 'number' && item.label === '마지막 순자산' ? `${manwonText(item.prev)} → ${manwonText(item.now)}` : `${item.prev} → ${item.now}`}</span>
      </div>)}
    </div>}

    <div className="drama-sides">
      {[['이전', prev, false], ['이번', cur, true]].map(([label, side, now]) => <article key={label} className={`drama-side ${now ? 'drama-side-now' : ''}`}>
        <small className="drama-side-label">{label} 선택 <span>{side.endAge}세</span></small>
        <h4>{side.card.title}</h4>
        <dl className="drama-side-facts"><div><dt>직업</dt><dd>{side.jobTitle ?? '없음'}</dd></div><div><dt>순자산</dt><dd>{side.netWorth != null ? manwonText(side.netWorth) : '미기록'}</dd></div><div><dt>자녀</dt><dd>{side.children}명</dd></div><div><dt>집 · 반려동물</dt><dd>{side.homes}채 · {side.pets}마리</dd></div></dl>
      </article>)}
    </div>

    {(prev.series.length > 0 && cur.series.length > 0) && <div className="drama-chart"><h4>순자산이 어떻게 갈라졌나 <small className="muted">(2026년 돈 가치)</small></h4>
      <p className="muted small"><span className="drama-key drama-key-prev" />이전 선택 <span className="drama-key drama-key-now" />이번 선택</p>
      <Overlay chart={drama.chart} /></div>}

    <h4>항목별 비교</h4>
    <div className="drama-table-controls"><span>달라진 항목 {changedRows.length}개</span><label className="inline"><input type="checkbox" checked={onlyChanged} onChange={(e) => setOnlyChanged(e.target.checked)} />달라진 항목만</label></div>
    <div className="result-table-wrap"><table className="result-table drama-table">
      <thead><tr><th>항목</th><th>이전 선택</th><th>이번 선택</th><th>차이</th></tr></thead>
      <tbody>{shownRows.map((r) => <tr key={r.label} className={`drama-row-${r.tone} ${r.tone !== 'same' ? 'drama-row-changed' : ''}`}><th>{r.label}</th><td>{r.prev}</td><td><b>{r.now}</b></td><td className="drama-diff"><span>{r.tone === 'up' ? '↑ ' : r.tone === 'down' ? '↓ ' : ''}{r.diff}</span></td></tr>)}{!shownRows.length && <tr><td colSpan="4" className="drama-empty-row">달라진 항목이 없습니다.</td></tr>}</tbody>
    </table></div>

    {drama.timeline.length > 0 && <><h4>큰 사건을 나란히</h4>
      <div className="drama-timeline">
        <div className="drama-tl-head"><span>이전 선택</span><span>나이</span><span>이번 선택</span></div>
        {drama.timeline.map((m) => <div key={m.age} className={`drama-tl-row ${m.fork ? 'drama-tl-fork' : ''} ${m.same ? 'drama-tl-same' : ''}`}>
          <span>{m.prev.join(' · ') || '—'}</span><b>{m.age}세</b><span>{m.now.join(' · ') || '—'}</span>
        </div>)}
      </div></>}

    <p className="muted small">같은 출생 조건과 같은 시드에서 시작했어요. 갈라진 뒤의 차이는 이어진 결과이고, 하나하나의 원인을 뜻하지는 않아요.{drama.sameRules ? '' : ' 기준 데이터 또는 규칙 버전이 달라 선택만의 차이로 읽을 수는 없어요.'}</p>
  </section>;
}
